import { useEffect, useState } from "react";
import { FileText, ExternalLink, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { fmtNaira } from "@/lib/api";

const KIND_LABEL = {
  quote: { label: "Quotation", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  invoice: { label: "Invoice", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function AdminDocuments() {
  const [data, setData] = useState({ documents: [], counts: { quotes: 0, invoices: 0, total_value_ngn: 0 } });
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = kind ? `?kind=${kind}` : "";
      const r = await adminApi.get(`/admin/documents${q}`);
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [kind]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-documents">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-600" /> Documents
        </h1>
        <p className="text-sm text-kudi-ink/60 mt-1">All quotations and invoices generated by traders.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <button onClick={() => setKind("")} className={`p-3 rounded-xl border text-left transition ${kind === "" ? "ring-2 ring-kudi-terracotta ring-offset-1" : ""} bg-white border-border`} data-testid="doc-filter-all">
          <div className="text-xs text-kudi-ink/60">All documents</div>
          <div className="text-2xl font-bold tabular-nums">{data.counts.quotes + data.counts.invoices}</div>
        </button>
        <button onClick={() => setKind("quote")} className={`p-3 rounded-xl border text-left transition ${kind === "quote" ? "ring-2 ring-kudi-terracotta ring-offset-1" : ""} bg-indigo-50 border-indigo-200 text-indigo-800`} data-testid="doc-filter-quote">
          <div className="text-xs opacity-80">Quotations</div>
          <div className="text-2xl font-bold tabular-nums">{data.counts.quotes}</div>
        </button>
        <button onClick={() => setKind("invoice")} className={`p-3 rounded-xl border text-left transition ${kind === "invoice" ? "ring-2 ring-kudi-terracotta ring-offset-1" : ""} bg-emerald-50 border-emerald-200 text-emerald-800`} data-testid="doc-filter-invoice">
          <div className="text-xs opacity-80">Invoices</div>
          <div className="text-2xl font-bold tabular-nums">{data.counts.invoices}</div>
        </button>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-kudi-ink/40 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : data.documents.length === 0 ? (
          <div className="p-8 text-center text-kudi-ink/50">No documents.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-kudi-cream text-xs text-kudi-ink/60 uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Kind</th>
                <th className="text-left px-3 py-2 font-medium">Number</th>
                <th className="text-left px-3 py-2 font-medium">Trader</th>
                <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Customer</th>
                <th className="text-right px-3 py-2 font-medium">Total</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Created</th>
                <th className="text-right px-3 py-2 font-medium">Files</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.documents.map((d) => {
                const k = KIND_LABEL[d.kind] || { label: d.kind, tone: "bg-gray-100" };
                return (
                  <tr key={d.id} className="hover:bg-accent/30 transition" data-testid={`doc-row-${d.id}`}>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${k.tone}`}>{k.label}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{d.number || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium truncate max-w-[160px]">{d.user?.business_name || d.user?.owner_name || "—"}</div>
                      <div className="text-kudi-ink/50 truncate max-w-[160px]">{d.user?.email || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell truncate max-w-[160px]">{d.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtNaira(d.total || 0)}</td>
                    <td className="px-3 py-2 text-xs capitalize">{d.status || "draft"}</td>
                    <td className="px-3 py-2 text-xs text-kudi-ink/50 hidden md:table-cell whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <a href={`/api/documents/${d.id}.pdf`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                        PDF <ExternalLink className="w-3 h-3" />
                      </a>
                      {" · "}
                      <a href={`/api/documents/${d.id}.png`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">PNG</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-kudi-ink/50">
        Total value across visible documents: <span className="font-semibold text-kudi-ink">{fmtNaira(data.counts.total_value_ngn || 0)}</span>
      </p>
    </div>
  );
}
